import { delay, calculateMedian } from './helpers.js';
const { UNSENT, OPENED, HEADERS_RECEIVED } = XMLHttpRequest;

export function ntp({ clientStart, serverTime, clientEnd }) {
  const roundTripTime = clientEnd - clientStart;

  const clientOffset =
    (serverTime - clientStart + (serverTime - clientEnd)) / 2;

  return {
    roundTripTime,
    clientOffset,
  };
}

export default class TimeSync {
  constructor({
    retries = 10,
    timeout = 4000,
    delay = 100,
    sampleDelay = 400,
    samples = 3,
  } = {}) {
    this.offsets = [0];
    this.retries = retries;
    this.timeout = timeout;
    this.samples = samples;
    this.delay = delay; // delay multiplier between syncs
    this.retryCount = 0;
    this.sampleCount = 0;
    this.reSyncCount = 0;
    this.sampleDelay = sampleDelay;
    this.waitBetweenFullRetries = 5000;
    this.url = '/now';
  }

  get offset() {
    return calculateMedian(this.offsets);
  }

  get synced() {
    return Boolean(this.syncedAt);
  }

  get isExtreme() {
    return Math.abs(this.offset) >= 1000;
  }

  async reSync() {
    if (this.syncing) return;

    this.offsets = [];
    this.retryCount = 0;
    this.reSyncCount++;

    this.syncing = true;
    await this.sampleSync();
    this.report();
    this.syncing = false;
  }

  /** @internal */
  async sample() {
    if (this.sampleCount >= this.samples) return true;

    this.sampleCount++;

    await delay(this.sampleDelay);
    await this.sync();
    await this.sample();
  }

  /** @internal */
  async sampleSync() {
    this.retryCount = 0;
    this.sampleCount = 0;

    if (!navigator.onLine) {
      this.scheduleARetry();

      throw new Error(
        `[TimeSync] Cannot sync because navigator is offline. Retry in ${this.waitBetweenFullRetries}`
      );
    }

    await this.sample();
  }

  report() {
    const msg = `[TimeSync] Sample(${this.sampleCount}x) offset ${this.offset} ms. Offsets: ${this.offsets}`;
    if (this.isExtreme) {
      console.warn(msg);
    }
  }

  /** @internal */
  scheduleARetry() {
    setTimeout(() => this.reSync(), this.waitBetweenFullRetries);
  }

  /** @internal */
  get delayBetweenSync() {
    return 200 + this.retryCount * this.delay * 0.7;
  }

  async sync() {
    this.retryCount++;

    try {
      await this.fetch();
    } catch (e) {
      if (this.retryCount >= this.retries) {
        this.scheduleARetry();

        throw new Error(
          `[TimeSync] Completely failed to sync. Schedule retry in ${this.waitBetweenFullRetries}`
        );
      }

      await delay(this.delayBetweenSync);
      await this.sync();
    }
  }

  /** @internal */
  onReadyStateChange(resolve, reject) {
    const { xhr } = this;
    const { readyState, status, responseText } = xhr;

    /* c8 ignore next */
    if (readyState === UNSENT) return;

    const success = status >= 200 && status < 300;

    if (readyState === OPENED) {
      this.clientStart = Date.now();
      return;
    }

    if (readyState === HEADERS_RECEIVED) {
      if (!success)
        return reject(
          new Error(`XHR Request failed with status ${status} ${responseText}`)
        );

      this.clientEnd = Date.now();
      this.serverTime = parseInt(xhr.getResponseHeader('X-T'), 10);

      this.syncedAt = this.serverTime;

      this.offsets.push(ntp(this).clientOffset);

      resolve();
    }
  }

  /** @internal */
  fetch() {
    const xhr = new XMLHttpRequest();
    this.xhr = xhr;

    return new Promise((resolve, reject) => {
      this.syncing = false;
      xhr.onreadystatechange = () => this.onReadyStateChange(resolve, reject);

      xhr.open('HEAD', this.url, true);
      xhr.timeout = this.timeout;
      xhr.ontimeout = e => reject(e);

      xhr.send();
    });
  }
}
